import React, { useState, useEffect } from "react";
import axios from "axios";
import { Icon } from "./Icon";

interface TwitchApp {
  id: string;
  type: "dashboard" | "bot";
  clientId: string;
  clientSecret: string;
  callbackUrl?: string;
  createdAt: string;
  updatedAt: string;
  isEditable: boolean;
}

interface TwitchAppsProps {
  authToken: string;
}

const TwitchApps: React.FC<TwitchAppsProps> = ({ authToken }) => {
  const [apps, setApps] = useState<TwitchApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingApp, setEditingApp] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    clientId: "",
    clientSecret: "",
    callbackUrl: "",
  });

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/twitch-apps", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setApps(response.data);
    } catch (error) {
      console.error("Error fetching Twitch apps:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (app: TwitchApp) => {
    if (!app.isEditable) return;

    setEditingApp(app.type);
    setFormData({
      clientId: app.clientId,
      clientSecret: "", // Always empty for security
      callbackUrl: app.callbackUrl || "",
    });
  };

  const handleSave = async (type: string) => {
    try {
      await axios.put(`/api/twitch-apps/${type}`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      await fetchApps();
      setEditingApp(null);
      setFormData({
        clientId: "",
        clientSecret: "",
        callbackUrl: "",
      });
    } catch (error: any) {
      console.error("Error saving Twitch app:", error);
      alert(
        error.response?.data?.message ||
          "Failed to save Twitch app configuration",
      );
    }
  };

  const handleCancel = () => {
    setEditingApp(null);
    setFormData({
      clientId: "",
      clientSecret: "",
      callbackUrl: "",
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
        Loading Twitch apps configuration...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
      <h2 style={{ color: "#e2e8f0", marginBottom: "30px" }}>
        Twitch Apps Configuration
      </h2>

      <div
        style={{
          backgroundColor: "#2d3748",
          padding: "20px",
          borderRadius: "8px",
          marginBottom: "20px",
          border: "1px solid #4a5568",
        }}
      >
        <div
          style={{ fontSize: "14px", color: "#cbd5e0", marginBottom: "15px" }}
        >
          <strong style={{ color: "#fbbf24" }}>
            <Icon
              name="info"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            Important:
          </strong>{" "}
          The Dashboard App credentials are read-only and must be configured
          through environment variables for security reasons. Only the Bot App
          credentials can be edited here.
        </div>
      </div>

      <div style={{ display: "grid", gap: "20px" }}>
        {apps.map((app) => (
          <div
            key={app.id}
            style={{
              backgroundColor: "#2d3748",
              padding: "25px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
            }}
          >
            {editingApp === app.type ? (
              <div>
                <h3
                  style={{
                    color: "#9146FF",
                    marginTop: 0,
                    marginBottom: "20px",
                  }}
                >
                  Edit {app.type === "dashboard" ? "Dashboard" : "Bot"} App
                </h3>
                <div style={{ display: "grid", gap: "15px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        color: "#cbd5e0",
                        fontSize: "14px",
                      }}
                    >
                      Client ID
                    </label>
                    <input
                      type="text"
                      value={formData.clientId}
                      onChange={(e) =>
                        setFormData({ ...formData, clientId: e.target.value })
                      }
                      placeholder="Twitch Client ID"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #4a5568",
                        borderRadius: "6px",
                        backgroundColor: "#1a202c",
                        color: "#e2e8f0",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        color: "#cbd5e0",
                        fontSize: "14px",
                      }}
                    >
                      Client Secret
                    </label>
                    <input
                      type="password"
                      value={formData.clientSecret}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          clientSecret: e.target.value,
                        })
                      }
                      placeholder="Leave empty to keep current secret"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #4a5568",
                        borderRadius: "6px",
                        backgroundColor: "#1a202c",
                        color: "#e2e8f0",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div
                    style={{ display: "flex", gap: "10px", marginTop: "10px" }}
                  >
                    <button
                      onClick={() => handleSave(app.type)}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#48BB78",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#6B7280",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    marginBottom: "15px",
                  }}
                >
                  <div>
                    <h3
                      style={{
                        color: "#e2e8f0",
                        margin: 0,
                        marginBottom: "5px",
                      }}
                    >
                      {app.type === "dashboard" ? "Dashboard App" : "Bot App"}
                    </h3>
                    <span
                      style={{
                        fontSize: "12px",
                        color: app.isEditable ? "#48BB78" : "#6B7280",
                        fontWeight: "500",
                      }}
                    >
                      {app.isEditable ? (
                        <>
                          <Icon
                            name="check"
                            size={12}
                            color="#10B981"
                            style={{
                              verticalAlign: "-2px",
                              marginRight: "4px",
                            }}
                          />
                          Editable
                        </>
                      ) : (
                        <>
                          <Icon
                            name="lock"
                            size={12}
                            style={{
                              verticalAlign: "-2px",
                              marginRight: "4px",
                            }}
                          />
                          Read-only (Environment Variables)
                        </>
                      )}
                    </span>
                  </div>
                  {app.isEditable && (
                    <button
                      onClick={() => handleEdit(app)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#4a5568",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div style={{ fontSize: "14px", color: "#a0aec0" }}>
                  <p style={{ margin: "8px 0" }}>
                    <strong style={{ color: "#cbd5e0" }}>Client ID:</strong>{" "}
                    <code
                      style={{
                        backgroundColor: "#1a202c",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        fontFamily: "monospace",
                      }}
                    >
                      {app.clientId}
                    </code>
                  </p>
                  <p style={{ margin: "8px 0" }}>
                    <strong style={{ color: "#cbd5e0" }}>Client Secret:</strong>{" "}
                    <code
                      style={{
                        backgroundColor: "#1a202c",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        fontFamily: "monospace",
                      }}
                    >
                      {app.clientSecret}
                    </code>
                  </p>
                  {app.callbackUrl && (
                    <p style={{ margin: "8px 0" }}>
                      <strong style={{ color: "#cbd5e0" }}>
                        Callback URL:
                      </strong>{" "}
                      <code
                        style={{
                          backgroundColor: "#1a202c",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          fontFamily: "monospace",
                          fontSize: "12px",
                        }}
                      >
                        {app.callbackUrl}
                      </code>
                    </p>
                  )}
                  <p
                    style={{
                      margin: "8px 0",
                      fontSize: "12px",
                      color: "#718096",
                    }}
                  >
                    <strong>Last Updated:</strong>{" "}
                    {new Date(app.updatedAt).toLocaleString()}
                  </p>
                </div>

                {app.type === "bot" && !app.clientId && (
                  <div
                    style={{
                      marginTop: "15px",
                      padding: "10px",
                      backgroundColor: "#1a202c",
                      borderRadius: "4px",
                      fontSize: "13px",
                      color: "#fbbf24",
                    }}
                  >
                    <strong>
                      <Icon
                        name="alert-triangle"
                        size={13}
                        style={{ verticalAlign: "-2px", marginRight: "6px" }}
                      />
                      Not Configured:
                    </strong>{" "}
                    Bot app credentials must be configured before bot tokens can
                    be refreshed automatically.
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: "30px",
          padding: "20px",
          backgroundColor: "#1a202c",
          borderRadius: "8px",
          border: "1px solid #4a5568",
        }}
      >
        <h4 style={{ color: "#48BB78", marginTop: 0, marginBottom: "15px" }}>
          Usage Information
        </h4>
        <div style={{ fontSize: "14px", color: "#cbd5e0", lineHeight: "1.6" }}>
          <p style={{ margin: "0 0 10px 0" }}>
            <strong>Dashboard App:</strong> Used for user authentication when
            logging into the dashboard. These credentials must match your Twitch
            application&apos;s OAuth settings.
          </p>
          <p style={{ margin: "0 0 10px 0" }}>
            <strong>Bot App:</strong> Used for bot authentication and automatic
            token refresh. Create a separate Twitch application for your bot to
            ensure proper isolation and permissions.
          </p>
          <p style={{ margin: "0", fontSize: "13px", color: "#a0aec0" }}>
            Remember to set the correct OAuth redirect URLs in your Twitch
            applications:
            <br />• Dashboard: {window.location.origin}/api/auth/twitch/callback
            <br />• Bot: {window.location.origin}/api/auth/bot-callback
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwitchApps;
